import { SagaIterator } from 'redux-saga';
import { call, put, takeEvery } from 'redux-saga/effects';
import {
  FetchDraftsRequestedAction,
  fetchDraftsFailed,
  FetchDraftsFailedAction,
  fetchDraftsSucceeded,
  draftsAvailable,
  FetchDraftByParentIdRequestedAction,
  fetchDraftByParentIdSucceeded,
  fetchDraftByParentIdFailed,
} from '../actions/DraftsActions';
import { requestFailed } from '../../app/actions/RequestFailedAction';
import { Messages } from '../../codegen/Localize';
import { apiPerformanceTimer, HomeApiResult } from '../../home/utils/ApiPerformanceTimer';
import { generateUuid } from '../../home/utils/uuidGenerator';
import { ChannelTypes } from '../../home/types/ChannelTypes';
import { getDraftsRequest, discardDraftRequest, getDraftByParentIdRequest } from '../requests/DraftsRequests';
import { Draft } from '../types/DraftsTypes';
import { discardDraftSucceededAction, DiscardDraftRequestedAction, discardDraftsFailed, removeDraftFromContentInfo } from '../actions/DiscardDraftAction';
import {
  DiscardDraftRequested,
  DraftFetchByParentIdRequested,
  DraftsFetchInfoFailed,
  DraftsFetchInfoRequested,
} from '../../app/actions/AppActionTypes';
import { FeatureFlags } from '../../ts/FeatureFlags';

export function* rootDraftsSaga(): SagaIterator {
  yield takeEvery(DraftsFetchInfoRequested, handleDraftsRequestedAction);
  yield takeEvery(DraftsFetchInfoFailed, handleDraftsFailedAction);
  yield takeEvery(DiscardDraftRequested, handleDiscardDraftAction);
  yield takeEvery(DraftFetchByParentIdRequested, handleDraftByIdRequestedAction);
}

export function* handleDraftsRequestedAction(action: FetchDraftsRequestedAction): SagaIterator {
  try {
    const uuid = generateUuid();
    yield call(
      apiPerformanceTimer,
      fetchDraftsWithPerformanceTimer(action),
      action.spaceType,
      ChannelTypes.text,
      uuid);
  } catch (e) {
    yield put(fetchDraftsFailed(e));
  }
}

export function* handleDraftsFailedAction(action: FetchDraftsFailedAction): SagaIterator {
  //TODO: remove request failed once we are doing something more useful
  yield put(requestFailed(action.error, Messages.errorUnknown()));
}

export function* handleDraftByIdRequestedAction(action: FetchDraftByParentIdRequestedAction): SagaIterator {
  try {
    const draft = yield call(getDraftByParentIdRequest, action);
    if (FeatureFlags.enableShowWorkbookAutoSaveOnVPC) {
      yield put(fetchDraftByParentIdSucceeded(draft));
    }
  } catch (e) {
    yield put(fetchDraftByParentIdFailed(e));
  }
}

export function* handleDiscardDraftAction(action: DiscardDraftRequestedAction): SagaIterator {
  try {
    const contentId = yield call(discardDraftRequest, action);
    yield put(discardDraftSucceededAction(action.draftLuid));
    yield put(removeDraftFromContentInfo(action.parentLuid, action.contentType));
  }
  catch (e) {
  yield put(discardDraftsFailed(e));
  }
}

/**
 * High order function to return the function that uses the local action
 * without having to pass it down to the ApiPerformanceTimer
 * @param action
 */
export function fetchDraftsWithPerformanceTimer(action: FetchDraftsRequestedAction) {
  return function* fetchDrafts(result: HomeApiResult) {
    const drafts: Draft[] = yield call(getDraftsRequest, action);
    yield put(fetchDraftsSucceeded(drafts));
    yield put(draftsAvailable(drafts));
    result.contentLength = drafts.length;
  };
}
